import type { TExample } from '@atlas/uicorn/preview/modules';
import { useRef } from 'react';
import * as React from 'react';
import styles from './Dialog.module.css';
import cn from 'classnames';
import useRefCallback from 'use-ref-callback';
import { type TDialogElevation } from '../System/StylesProvider';

const Dialog = React.memo(
    React.forwardRef<TDialogRef, TDialogProps>(function Dialog(props, ref): React.ReactElement {
        const { elevation = 'extraLow', modal = false, onClick, className, ...dialogProps } = props;

        const handleClick = useRefCallback(function preventClickOnDialogPropagatingToParent(e) {
            e.stopPropagation();
            onClick?.(e);
        });

        return (
            <dialog
                {...dialogProps}
                className={cn(styles.dialog, className)}
                data-elevation={elevation}
                data-modal={modal}
                onClick={handleClick}
                ref={ref}
            />
        );
    }),
);

export default Dialog;

// #region Types

export type TDialogRef = HTMLDialogElement;

export type TDialogProps = React.DialogHTMLAttributes<HTMLDialogElement> & {
    elevation?: TDialogElevation;
    modal?: boolean;
};

// #endregion

// #region Examples

export const Example: TExample = {
    containerStyle: {
        maxWidth: 600,
    },
    render(props) {
        const ref = useRef<HTMLDialogElement>(null);

        return (
            <>
                <div>
                    Laboris deserunt Lorem velit do officia irure eiusmod qui adipisicing proident cillum. Exercitation
                    ex officia dolor ullamco aliquip occaecat nostrud voluptate ut esse exercitation laboris id sit
                    nostrud. Commodo commodo amet commodo ut consequat reprehenderit cillum nostrud. Ea do pariatur
                    dolor elit do quis officia aliqua non labore aute ut laborum enim. Nostrud adipisicing qui aliqua
                    sit tempor anim veniam fugiat minim nulla veniam laboris. Ea ad eu ipsum mollit quis commodo labore
                    nulla nisi.
                </div>
                <Dialog
                    open={props.open}
                    elevation={props.elevation}
                    children={
                        <div style={{ maxWidth: 240, textAlign: 'justify' }}>
                            Eu sit dolor nostrud officia cillum ea. Eu esse non nulla esse tempor deserunt. Eu eu veniam
                            exercitation laboris incididunt.
                        </div>
                    }
                    style={{ margin: 'auto' }}
                    ref={ref}
                />
            </>
        );
    },
    controls: {
        elevation: ['extraLow', 'low', 'medium', 'high'],
        open: { type: 'boolean', initial: true },
    },
    handlers: {
        onClick: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=0-1&mode=design&t=Hu2LDZwLvF5nHq01-0',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Dialog', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
